import DialogContent from '@mui/material/DialogContent'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextDateField } from 'Components/reusable/DataContext/fields/DataEditContextDateField'
import { DataEditContextCheckField } from 'Components/reusable/DataContext/fields/DataEditContextCheckField'
import { DataEditContextNullableNumberField } from 'Components/reusable/DataContext/fields/DataEditContextNullableNumberField'
import Grid from '@mui/material/Grid2'
import { combinedSearchApi } from 'api/CombinedSearchAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { useNotificationPopup } from 'Components/reusable/Notification'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import colors from 'constants/colors'

export interface IAssignmentProposal {
  city: string
  remoteAllowed: boolean
  percent: number | null
  pricePerHour: number | null
  startDate: Date | null
  duration: number | null
  extensionPossible: boolean
  information: string
}

/** @notExported  */
interface IAssignmentProposalModalProps {
  /** Modal close function. */
  onClose: () => void
  /** Anonymous ID. */
  anonymousId?: string
  /** Submit indicator. */
  submitIndicator: boolean
  /** Reset submit indicator. */
  resetSubmit: () => void
  /** Assignment proposal data. */
  data: IAssignmentProposal
}

/**
 * Assignment proposal modal.
 *
 * @returns Modal component.
 * @notExported
 */
const AssignmentProposalModal: React.FC<IAssignmentProposalModalProps> = ({
  onClose,
  anonymousId,
  submitIndicator,
  resetSubmit,
  data,
}) => {
  const { t } = useTranslation()
  const { setNotificationPopup } = useNotificationPopup()
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        if (submitIndicator && !anonymousId) {
          await combinedSearchApi.request(data, controller)
          {
            setNotificationPopup({
              message: t('search.request.success'),
              type: 'success',
              duration: 'short',
            })
            onClose()
          }
        }

        if (submitIndicator && anonymousId) {
          await combinedSearchApi.propose(anonymousId, data, controller)
          {
            setNotificationPopup({
              message: t('search.propose.success'),
              type: 'success',
              duration: 'short',
            })
            onClose()
          }
        }
      } catch (err) {
        setBackendError(err as IError)
        resetSubmit()
      }
    })()

    return () => {
      controller.abort()
    }
  }, [submitIndicator, anonymousId, data, onClose, resetSubmit, setNotificationPopup, t])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent sx={{ backgroundColor: colors.secondaryBackground }}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid size={6}>
          <DataEditContextTextField<IAssignmentProposal> field="city" fullWidth insetLabel />
        </Grid>
        <Grid size={6}>
          <DataEditContextCheckField<IAssignmentProposal> field="remoteAllowed" />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={6}>
          <DataEditContextNullableNumberField<IAssignmentProposal> field="percent" fullWidth insetLabel />
        </Grid>
        <Grid size={6}>
          <DataEditContextNullableNumberField<IAssignmentProposal> field="pricePerHour" fullWidth insetLabel />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={6}>
          <DataEditContextDateField<IAssignmentProposal>
            field="startDate"
            dateResolution="day"
            start={true}
            insetLabel
          />
        </Grid>
        <Grid size={6}>
          <DataEditContextNullableNumberField<IAssignmentProposal> field="duration" fullWidth insetLabel />
        </Grid>
      </Grid>
      <DataEditContextCheckField<IAssignmentProposal> field="extensionPossible" />
      <DataEditContextTextField<IAssignmentProposal> field="information" multiline grow rows={6} fullWidth insetLabel />
    </DialogContent>
  )
}

export default newWithEditModal(AssignmentProposalModal)
